/**
 * HelcimSubscriptionDetailModal
 * Tabbed detail view: Subscription Overview | Billing History | Add-ons
 * Matches Helcim dashboard layout exactly.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  IonModal,
  IonButton,
  IonContent,
  IonGrid,
  IonIcon,
  IonBadge,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonItem,
  IonCard,
  IonList,
} from '@ionic/react';
import {
  closeOutline,
  chevronDownOutline,
  chevronUpOutline,
  chevronForwardOutline,
  checkmarkCircleOutline,
  pauseCircleOutline,
  closeCircleOutline,
  timeOutline,
  ellipseOutline,
  ellipsisVertical,
  trashOutline,
  banOutline,
  linkOutline,
  createOutline,
  extensionPuzzleOutline,
} from 'ionicons/icons';
import type { HelcimSubscription } from '@rsmaids/shared/services/api/helcim/helcim';
import styles from './HelcimSubscriptionDetailModal.module.scss';
import BasePageWithHeader from './BasePageWithHeader';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../i18n/dynamic';
import { intlLocale } from '../i18n/locale';

interface Props {
  subscription: HelcimSubscription | null;
  isOpen: boolean;
  onClose: () => void;
  planName?: string;
  customerName?: string;
  cardData?: Record<string, unknown> | null;
  onContactClick?: () => void;
  onManage?: (sub: HelcimSubscription, action: 'pause' | 'resume' | 'cancel' | 'delete' | 'edit') => void;
}

// ── Card brand logos (copied from HelcimPaymentsModal) ───────────────────────
const LOGO_STYLE: React.CSSProperties = { display: 'block', width: 34, height: 20, borderRadius: 3 };

const VisaLogo = () => {
  const { t } = useTranslation();
  return (
  <img src="/Visa_Brandmark_Blue_RGB_2021.svg" alt={t('components.HelcimSubscriptionDetailModal.visa')} style={{ ...LOGO_STYLE, objectFit: 'contain', background: 'white', border: '0.5px solid rgba(0,0,0,0.12)' }} />
);
};
const MastercardLogo = () => (
  <svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" style={{ ...LOGO_STYLE, background: '#252525' }}>
    <rect width="38" height="24" rx="4" fill="#252525" />
    <circle cx="14" cy="12" r="7" fill="#EB001B" />
    <circle cx="24" cy="12" r="7" fill="#F79E1B" />
    <path d="M19 6.8a7 7 0 0 1 0 10.4A7 7 0 0 1 19 6.8z" fill="#FF5F00" />
  </svg>
);
const AmexLogo = () => {
  const { t } = useTranslation();
  return (
  <img src="/AXP.svg" alt={t('components.HelcimSubscriptionDetailModal.americanExpress')} style={{ ...LOGO_STYLE, objectFit: 'cover' }} />
);
};
const DiscoverLogo = () => {
  const { t } = useTranslation();
  return (
  <svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" style={{ ...LOGO_STYLE, border: '0.5px solid rgba(0,0,0,0.1)' }}>
    <defs>
      <linearGradient id="discGrad2" x1="0" y1="0" x2="1" y2="0">
        <stop offset="50%" stopColor="white" />
        <stop offset="100%" stopColor="#F76F20" />
      </linearGradient>
    </defs>
    <rect width="38" height="24" rx="4" fill="url(#discGrad2)" />
    <circle cx="29" cy="12" r="7" fill="#F76F20" />
    <text x="3" y="14" fontFamily="Arial, sans-serif" fontSize="5.5" fontWeight="800" fill="#333" letterSpacing="0.2">{t('components.HelcimSubscriptionDetailModal.discover')}</text>
  </svg>
);
};
const GenericCardLogo = () => (
  <svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" style={LOGO_STYLE}>
    <rect width="38" height="24" rx="4" fill="rgba(120,120,128,0.2)" stroke="rgba(120,120,128,0.3)" strokeWidth="1" />
    <rect x="4" y="7" width="12" height="8" rx="2" fill="rgba(120,120,128,0.5)" />
    <rect x="4" y="16" width="6" height="2" rx="1" fill="rgba(120,120,128,0.4)" />
    <rect x="12" y="16" width="6" height="2" rx="1" fill="rgba(120,120,128,0.4)" />
  </svg>
);
function CardBrandLogo({ brand }: { brand: string }) {
  const b = brand.toLowerCase().replace(/\s/g, '');
  if (b === 'vi' || b === 'visa') return <VisaLogo />;
  if (b === 'mc' || b === 'mastercard') return <MastercardLogo />;
  if (b === 'ax' || b === 'am' || b === 'amex' || b === 'americanexpress') return <AmexLogo />;
  if (b === 'di' || b === 'disc' || b === 'discover') return <DiscoverLogo />;
  return <GenericCardLogo />;
}

function pickCardField(card: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = String(card[k] ?? '').trim();
    if (v) return v;
  }
  return '';
}

const STATUS_ICON: Record<string, string> = {
  active:      checkmarkCircleOutline,
  paused:      pauseCircleOutline,
  cancelled:   closeCircleOutline,
  term_ending: timeOutline,
  term_ended:  closeCircleOutline,
};
const STATUS_COLOR: Record<string, string> = {
  active:      'success',
  paused:      'tertiary',
  cancelled:   'medium',
  term_ending: 'warning',
  term_ended:  'medium',
};

// Tone-class lookup — ion-badge's default (non `.ion-color`) :host rule reads
// --background/--color directly, so a CSS-module tone class is applied
// instead of Ionic's `color` prop, matching HelcimPaymentsModal's/
// HelcimTransactionDetailModal's badge system.
const BADGE_TONE_CLASS: Record<string, string> = {
  success: 'badgeSuccess',
  danger: 'badgeDanger',
  warning: 'badgeWarning',
  medium: 'badgeMedium',
  tertiary: 'badgeTertiary',
};

interface OverflowMenuOption {
  value: string;
  label: string;
  icon: string;
  destructive?: boolean;
}

// Same anchored-portal mechanism as HelcimSubscriptionsModal.tsx's
// usePortalDropdownPosition/PortalDropdown — portals an IonCard into an
// in-page portalRef container instead of relying on CardActionsPopover's
// IonPopover, for consistency with the subscriptions list's row-menu.
type PortalDropdownRect = { top: number; right: number };

function useOverflowMenuPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  const [rect, setRect] = useState<PortalDropdownRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PortalDropdownRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      return { top: box.bottom - anchor.top + 4, right: anchor.right - box.right };
    };
    const applyRect = (r: PortalDropdownRect) => {
      const card = cardRef.current as HTMLElement | null;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.right = `${r.right}px`;
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    const handleScroll = () => { const r = computeRect(); if (r) applyRect(r); };
    const handleResize = () => { const r = computeRect(); if (r) { setRect(r); applyRect(r); } };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose]);

  return rect;
}

const OverflowMenu: React.FC<{
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  options: OverflowMenuOption[];
  onSelect: (value: string) => void;
}> = ({ isOpen, anchorRef, portalRef, onClose, options, onSelect }) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = useOverflowMenuPosition(isOpen, anchorRef, portalRef, cardRef, onClose);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  return createPortal(
    <>
      <div className={styles.dropdownBackdrop} onClick={onClose} />
      <IonCard ref={cardRef} className={styles.customDropdownList} style={{ ...rect, position: 'absolute' }}>
        <IonList lines="none" className={styles.customDropdownListInner}>
          {options.map(opt => (
            <IonItem
              key={opt.value}
              button
              lines="none"
              detail={false}
              className={`${styles.customDropdownItem} ${opt.destructive ? styles.customDropdownItemDestructive : ''}`}
              onClick={() => onSelect(opt.value)}
            >
              <IonIcon icon={opt.icon} slot="start" className={styles.customDropdownItemIcon} />
              <IonLabel>{opt.label}</IonLabel>
            </IonItem>
          ))}
        </IonList>
      </IonCard>
    </>,
    portalNode,
  );
};

const fmt = (val?: number) =>
  val != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val) : '�';

const fmtDate = (d?: string) => {
  if (!d) return '—';
  // Helcim returns dates as '2026-05-12 10:30:00' (space) or '2026-05-12' (date-only)
  const iso = d.replace(' ', 'T');
  const parsed = new Date(iso.includes('T') ? iso : iso + 'T12:00:00');
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
};

// Compact numeric form ("8/19/26") for the Billing History table's two date
// columns — on narrow screens "Aug 19, 2026" is too long to fit consistently
// (some months wrap, some don't, at the same width), so a fixed-width numeric
// format is swapped in via CSS at small breakpoints instead.
const fmtDateCompact = (d?: string) => {
  if (!d) return '—';
  const iso = d.replace(' ', 'T');
  const parsed = new Date(iso.includes('T') ? iso : iso + 'T12:00:00');
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(intlLocale(), { month: 'numeric', day: 'numeric', year: '2-digit' });
};

const HelcimSubscriptionDetailModal: React.FC<Props> = ({
  subscription: sub,
  isOpen,
  onClose,
  planName,
  customerName: customerNameProp,
  cardData = null,
  onContactClick,
  onManage,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'overview' | 'billing' | 'addons'>('overview');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const overflowTriggerRef = useRef<HTMLIonButtonElement>(null);
  const overflowPortalRef = useRef<HTMLDivElement>(null);

  // Reset the tab/expanded-section view whenever the modal opens on a
  // (possibly different) subscription — computed during render (React's
  // "adjust state when a dependency changes" pattern) rather than as
  // synchronous setState calls inside an effect.
  const [prevTabResetKey, setPrevTabResetKey] = useState<{ isOpen: boolean; subId: unknown }>({ isOpen, subId: sub?.id });
  if (prevTabResetKey.isOpen !== isOpen || prevTabResetKey.subId !== sub?.id) {
    setPrevTabResetKey({ isOpen, subId: sub?.id });
    if (isOpen) {
      setActiveTab('overview');
      setDetailsOpen(false);
      setBreakdownOpen(false);
      setShowOverflowMenu(false);
    }
  }

  if (!sub) return null;

  const raw           = sub as Record<string, unknown>;
  const status        = (sub.status ?? 'unknown').toLowerCase();
  const maxCycles     = sub.maxCycles ?? 0;
  const billingCycle  = `${sub.timesBilled ?? 0}/${maxCycles === 0 ? '\u221e' : maxCycles}`;
  const billingPeriod = (raw['billingPeriod'] as string | undefined)?.toUpperCase() ?? t('components.HelcimSubscriptionDetailModal.month');
  const totalAmount   = (raw['totalAmount']  as number | undefined) ?? sub.recurringAmount;
  const taxAmount     = raw['taxAmount']     as number | undefined;
  const setupAmount   = raw['setupAmount']   as number | undefined;
  const payments      = (raw['payments']     as Array<Record<string, unknown>> | undefined) ?? [];

  const displayName = customerNameProp
    || (raw['customerName'] as string | undefined)
    || sub.customerCode
    || '\u2014';
  const initials = String(displayName)
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

  // Payment method on file — card or bank account
  const isBankAccount = cardData?.['_type'] === 'bank';
  const bankAcctNum  = isBankAccount ? String(
    cardData?.['bankAccountNumberL4'] ?? cardData?.['bankAccountNumber'] ?? cardData?.['accountNumber'] ?? ''
  ) : '';
  const bankLast4    = bankAcctNum.replace(/\D/g, '').slice(-4) || bankAcctNum.slice(-4);
  const bankAcctType = isBankAccount ? String(cardData?.['bankAccountType'] ?? cardData?.['accountType'] ?? '') : '';
  const rawCardNum = !isBankAccount && cardData ? pickCardField(cardData, 'cardNumber', 'cardF6L4', 'maskedPan', 'pan') : '';
  const cardFmtNum = rawCardNum.length === 10
    ? `${rawCardNum.slice(0,4)} ${rawCardNum.slice(4,6)}** **** ${rawCardNum.slice(6)}`
    : rawCardNum.length >= 4 ? `**** **** **** ${rawCardNum.slice(-4)}` : '';
  const last4      = rawCardNum.slice(-4);
  const rawBrand   = !isBankAccount && cardData ? pickCardField(cardData, 'cardType', 'card_type', 'cardBrand', 'brand') : '';
  const cardBrand  = rawBrand || (() => {
    const first = rawCardNum[0];
    if (first === '4') return 'VI';
    if (first === '5' || first === '2') return 'MC';
    if (first === '3') return 'AX';
    if (first === '6') return 'DI';
    return '';
  })();
  const cardHolderName = !isBankAccount && cardData ? pickCardField(cardData, 'cardHolderName', 'holderName', 'name') : '';
  const cardExpiry = !isBankAccount && cardData ? pickCardField(cardData, 'cardExpiry', 'card_expiry', 'expiryDate') : '';
  const expFmt     = cardExpiry.length === 4 ? `${cardExpiry.slice(0,2)}/${cardExpiry.slice(2)}` : cardExpiry;

  const expiryDate = maxCycles > 0 && sub.dateActivated
    ? (() => {
        try {
          const d = new Date(sub.dateActivated + 'T00:00:00');
          d.setMonth(d.getMonth() + maxCycles);
          return d.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
        } catch { return '\u2014'; }
      })()
    : null;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.modal}>
      <div ref={overflowPortalRef} className={styles.pickerPortalRoot} />
      <OverflowMenu
        isOpen={showOverflowMenu}
        anchorRef={overflowTriggerRef}
        portalRef={overflowPortalRef}
        onClose={() => setShowOverflowMenu(false)}
        onSelect={(action) => {
          setShowOverflowMenu(false);
          onManage?.(sub, action as 'pause' | 'resume' | 'cancel' | 'delete' | 'edit');
          onClose();
        }}
        options={[
          ...(status !== 'cancelled' ? [{ value: 'edit', label: t('components.HelcimSubscriptionDetailModal.editSubscription'), icon: createOutline }] : []),
          ...(status === 'paused' ? [{ value: 'resume', label: t('components.HelcimSubscriptionDetailModal.resumeSubscription'), icon: checkmarkCircleOutline }] : []),
          ...(status === 'active' ? [{ value: 'pause', label: t('components.HelcimSubscriptionDetailModal.pauseSubscription'), icon: pauseCircleOutline }] : []),
          ...(status !== 'cancelled' ? [{ value: 'cancel', label: t('components.HelcimSubscriptionDetailModal.cancelSubscription'), icon: banOutline }] : []),
          { value: 'delete', label: t('components.HelcimSubscriptionDetailModal.deleteSubscription'), icon: trashOutline, destructive: true },
        ]}
      />
      <BasePageWithHeader
        toolbarClassName={styles.toolbar}
        title={displayName}
        titleNode={
          <div className={styles.titleArea}>
            <IonBadge className={`${styles.statusBadge} ${styles[BADGE_TONE_CLASS[STATUS_COLOR[status] ?? 'medium']]}`}>
              <IonIcon icon={STATUS_ICON[status] ?? ellipseOutline} className={styles.statusIcon} />
              {{ term_ending: 'Term Ending', term_ended: 'Term Ended' }[status]
                ?? (sub.status ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1) : translateDynamic('Unknown'))}
            </IonBadge>
          </div>
        }
        startButtons={
          <IonButton fill="clear" className={styles.closeBtn} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        }
        endButtons={
          <IonButton
            fill="clear"
            className={styles.menuBtn}
            onClick={(e) => {
              overflowTriggerRef.current = e.currentTarget;
              setShowOverflowMenu(o => !o);
            }}
          >
            <IonIcon icon={ellipsisVertical} />
          </IonButton>
        }
      >
        <IonSegment
          value={activeTab}
          onIonChange={e => setActiveTab(e.detail.value as typeof activeTab)}
          className={styles.segment}
          scrollable
        >
          <IonSegmentButton value="overview">
            <IonLabel>
              <span className={styles.segmentLabelFull}>{t('components.HelcimSubscriptionDetailModal.subscriptionOverview')}</span>
              <span className={styles.segmentLabelCompact}>{t('components.HelcimSubscriptionDetailModal.overview')}</span>
            </IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="billing"><IonLabel>{t('components.HelcimSubscriptionDetailModal.billingHistory')}</IonLabel></IonSegmentButton>
          <IonSegmentButton value="addons"><IonLabel>{t('components.HelcimSubscriptionDetailModal.addOns')}</IonLabel></IonSegmentButton>
        </IonSegment>

      <IonContent className={styles.content}>
        <IonGrid className={styles.scrollContainer}>

        {activeTab === 'overview' && (
          <>
            <div className={styles.heroCard}>
              <span className={styles.heroPeriod}>{t('components.HelcimSubscriptionDetailModal.baseRateEvery', { billingPeriod })}</span>
              <span className={styles.heroAmount}>{fmt(sub.recurringAmount)}</span>
              <span className={styles.heroCurrency}>{t('components.HelcimSubscriptionDetailModal.usd')}</span>
              <IonItem
                button
                detail
                detailIcon={chevronForwardOutline}
                lines="none"
                className={styles.customerRow}
                onClick={onContactClick}
                style={onContactClick ? undefined : { '--detail-icon-opacity': 0.3 } as React.CSSProperties}
              >
                <div className={styles.customerAvatar}>{initials}</div>
                <span className={styles.customerName}>{displayName}</span>
              </IonItem>
            </div>

            <div className={styles.tilesRow}>
              <div className={styles.tile}>
                {isBankAccount ? (
                  <>
                    <span className={styles.tileLabel}>{t('components.HelcimSubscriptionDetailModal.bankAccount')}</span>
                    {bankLast4 ? (
                      <>
                        <span className={styles.tileCardNum}>•••• {bankLast4}</span>
                        {bankAcctType && <span className={styles.tileSub}>{bankAcctType}</span>}
                      </>
                    ) : (
                      <span className={styles.tilePlaceholder}>{t('components.HelcimSubscriptionDetailModal.bankOnFile')}</span>
                    )}
                  </>
                ) : (
                  <>
                    <span className={styles.tileLabel}>{t('components.HelcimSubscriptionDetailModal.creditCard')}</span>
                    {cardData && last4 ? (
                      <>
                        <div className={styles.tileCardRow}>
                          <span className={styles.tileCardNum}>{cardFmtNum || `•••• ${last4}`}</span>
                          <CardBrandLogo brand={cardBrand} />
                        </div>
                        {cardHolderName && <span className={styles.tileSub}>{cardHolderName}</span>}
                        {expFmt && <span className={styles.tileSub}>{t('components.HelcimSubscriptionDetailModal.expires', { expFmt })}</span>}
                      </>
                    ) : (
                      <span className={styles.tilePlaceholder}>{t('components.HelcimSubscriptionDetailModal.noCardOnFile')}</span>
                    )}
                  </>
                )}
              </div>
              <div className={styles.tile}>
                <span className={styles.tileLabel}>{t('components.HelcimSubscriptionDetailModal.nextBilling')}</span>
                <span className={styles.tileValue}>{fmt(totalAmount)}</span>
                <span className={styles.tileSub}>{t('components.HelcimSubscriptionDetailModal.on')} {fmtDate(sub.dateBilling)}</span>
              </div>
            </div>

            <div className={`${styles.accordion}${detailsOpen ? ` ${styles.accordionOpen}` : ''}`} onClick={() => setDetailsOpen(o => !o)}>
              <span className={styles.accordionTitle}>{t('components.HelcimSubscriptionDetailModal.subscriptionDetails')}</span>
              <IonIcon icon={detailsOpen ? chevronUpOutline : chevronDownOutline} className={styles.accordionChevron} />
            </div>
            {detailsOpen && (
              <div className={styles.accordionBody}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('components.HelcimSubscriptionDetailModal.planName')}</span>
                  <span className={styles.detailValue}>{translateDynamic(planName ?? '—')}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('components.HelcimSubscriptionDetailModal.expiring')}</span>
                  <span className={styles.detailValue}>{expiryDate ?? t('components.HelcimSubscriptionDetailModal.no')}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('components.HelcimSubscriptionDetailModal.billingCycle')}</span>
                  <span className={styles.detailValue}>{billingCycle}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('components.HelcimSubscriptionDetailModal.subscriptionId')}</span>
                  <span className={styles.detailValue}>{sub.id}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('components.HelcimSubscriptionDetailModal.activationDate')}</span>
                  <span className={styles.detailValue}>{fmtDate(sub.dateActivated)}</span>
                </div>
              </div>
            )}

            <div className={`${styles.accordion}${breakdownOpen ? ` ${styles.accordionOpen}` : ''}`} onClick={() => setBreakdownOpen(o => !o)}>
              <span className={styles.accordionTitle}>{t('components.HelcimSubscriptionDetailModal.billingBreakdown')}</span>
              <IonIcon icon={breakdownOpen ? chevronUpOutline : chevronDownOutline} className={styles.accordionChevron} />
            </div>
            {breakdownOpen && (
              <div className={styles.accordionBody}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t('components.HelcimSubscriptionDetailModal.baseRate')}</span>
                  <span className={styles.detailValue}>{fmt(sub.recurringAmount)}</span>
                </div>
                {setupAmount != null && setupAmount > 0 && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('components.HelcimSubscriptionDetailModal.setupFee')}</span>
                    <span className={styles.detailValue}>{fmt(setupAmount)}</span>
                  </div>
                )}
                {taxAmount != null && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>{t('components.HelcimSubscriptionDetailModal.tax')}</span>
                    <span className={styles.detailValue}>{taxAmount > 0 ? fmt(taxAmount) : t('components.HelcimSubscriptionDetailModal.noTax')}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'billing' && (
          payments.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>{t('components.HelcimSubscriptionDetailModal.noBillingHistory')}</p>
              <p className={styles.emptySub}>{t('components.HelcimSubscriptionDetailModal.paymentRecordsWillAppearHereOnce')}</p>
            </div>
          ) : (
            <div className={styles.historyWrap}>
              <div className={`${styles.historyRow} ${styles.historyHeader}`}>
                <span>{t('components.HelcimSubscriptionDetailModal.amount')}</span>
                <span>{t('components.HelcimSubscriptionDetailModal.paymentStatus')}</span>
                <span>{t('components.HelcimSubscriptionDetailModal.billingDate')}</span>
                <span>{t('components.HelcimSubscriptionDetailModal.paidDate')}</span>
              </div>
              {payments.map((p, i) => {
                const pStatus   = String(p['status'] ?? '').toLowerCase();
                const isSuccess = pStatus === 'approved' || pStatus === 'paid' || pStatus === 'successful';
                // p['amount'] is the base/recurring amount only — Helcim reports tax
                // (and any add-on) as separate fields on the same payment record, so
                // the actual amount charged/paid has to be summed from all three.
                const pBaseAmount = Number(p['amount'] ?? 0);
                const pTaxAmount  = Number(p['taxAmount'] ?? 0);
                const pAddOnAmount = Number(p['addOnAmount'] ?? 0);
                const pAmount   = pBaseAmount + pTaxAmount + pAddOnAmount;
                return (
                  <div key={i} className={`${styles.historyRow} ${styles.historyDataRow}`}>
                    <span className={styles.historyAmount}>{fmt(pAmount)}</span>
                    <span className={styles.historyStatusCell}>
                      <IonBadge className={`${styles.historyBadge} ${styles[BADGE_TONE_CLASS[isSuccess ? 'success' : 'medium']]}`}>
                        {isSuccess ? t('components.HelcimSubscriptionDetailModal.successful') : pStatus || '\u2014'}
                      </IonBadge>
                    </span>
                    <span className={styles.historyDate}>
                      <span className={styles.historyDateFull}>{fmtDate((p['dateDue'] ?? p['dateCreated']) as string | undefined)}</span>
                      <span className={styles.historyDateCompact}>{fmtDateCompact((p['dateDue'] ?? p['dateCreated']) as string | undefined)}</span>
                    </span>
                    <span className={styles.historyDate}>
                      <span className={styles.historyDateFull}>{fmtDate((p['dateProcessed'] ?? p['datePaid'] ?? p['dateSettled'] ?? p['dateCreated']) as string | undefined)}</span>
                      <span className={styles.historyDateCompact}>{fmtDateCompact((p['dateProcessed'] ?? p['datePaid'] ?? p['dateSettled'] ?? p['dateCreated']) as string | undefined)}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'addons' && (
          <div className={styles.addonsTab}>
            <div className={styles.addonsCard}>
              <div className={styles.addonsCardLeft}>
                <div className={styles.addonsIconWrap}>
                  <IonIcon icon={linkOutline} className={styles.addonsIcon} />
                </div>
                <div>
                  <p className={styles.addonsCardTitle}>{t('components.HelcimSubscriptionDetailModal.linkedAddOns')}</p>
                  <p className={styles.addonsCardSub}>{t('components.HelcimSubscriptionDetailModal.addOnsForThisSubscriber')}</p>
                </div>
              </div>
              <IonButton fill="outline" size="small" disabled className={styles.manageBtn}>{t('components.HelcimSubscriptionDetailModal.manageAddOns')}
              </IonButton>
            </div>
            <div className={styles.emptyState}>
              <IonIcon icon={extensionPuzzleOutline} className={styles.emptyIcon} />
              <p className={styles.emptyTitle}>{t('components.HelcimSubscriptionDetailModal.noAddOnsAdded')}</p>
              <p className={styles.emptySub}>{t('components.HelcimSubscriptionDetailModal.visitTheAddOnsSectionTo')}</p>
            </div>
          </div>
        )}
        </IonGrid>
      </IonContent>
    </BasePageWithHeader>
    </IonModal>
  );
};

export default HelcimSubscriptionDetailModal;
